/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Carousel, DatePicker, Form, Image, Input, Message, Modal, Popconfirm, Select, Table, TableColumnProps, Tag, Typography } from '@arco-design/web-react';
import dayjs from 'dayjs';
import { post, get } from '../requestNew';

import './index.scss';

import { DEFAULT_RISK_LEVEL, riskLevelOption, riskLevelTagMap } from '../utils';
import { Item } from './types';

const FormItem = Form.Item;
const Option = Select.Option;

function Composition() {
  const [form] = Form.useForm<Item>();
  const [pageSize, setPageSize] = useState(20);
  const [pageNum, setPageNum] = useState(1);
  const [total, setTotal] = useState(0);
  const [dataList, setDataList] = useState<Item[]>([]);
  const [commentVisible, setCommentVisible] = useState(false);
  const [commentList, setCommentList] = useState([]);
  const commentId = useRef(0);
  const currentCommentId = useRef<number | null>(null);
  const [notebookInfo, setNotebookInfo] = useState<any>(null);
  const [replyVisible, setReplyVisible] = useState(false);
  const [replyList, setReplyList] = useState<any[]>([]);
  const [riskLevel, setRiskLevel] = useState(DEFAULT_RISK_LEVEL);
  const isRiskQuery = riskLevel !== DEFAULT_RISK_LEVEL;

  const riskColumns: TableColumnProps[] = [
    {
      title: '风险评分',
      dataIndex: 'riskConfidence',
      width: 100,
      render: text => {
        if (text === null || text === undefined || text === '') {
          return '-';
        }
        const num = Number(text);
        return Number.isNaN(num) ? text : num.toFixed(2);
      }
    },
    {
      title: '风险等级',
      dataIndex: 'riskLevel',
      width: 100,
      render: text => {
        const info = riskLevelTagMap[text];
        if (!info) {
          return '-';
        }
        return (
          <Tag color={info.bg} style={{ color: info.text, border: 'none' }}>
            {info.label}
          </Tag>
        );
      }
    },
    {
      title: '风险描述',
      dataIndex: 'riskDescription',
      width: 200,
      render: text => text || '-'
    }
  ];

  const columns: TableColumnProps[] = [
    {
      title: '赛季',
      dataIndex: 'gameSession',
      width: 100
    },
    {
      title: '评论选手',
      dataIndex: 'gameName',
      width: 100
    },
    {
      title: '评论内容',
      dataIndex: 'content',
      width: 400
    },
    {
      title: '回复数',
      dataIndex: 'replyNum',
      width: 400,
      render: text => text || '-'
    },
    {
      title: '评论时间',
      dataIndex: 'createTime',
      width: 150,
      render: text => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    ...(isRiskQuery ? riskColumns : []),
    {
      title: '操作',
      dataIndex: 'action',
      width: 150,
      render: (_, record: any) => (
        <>
          {isRiskQuery && (
            <Popconfirm
              focusLock
              title="提示"
              content="是否确认解除该评论风险？"
              onOk={() => confirmRiskComment(record)}
            >
              <Button className="btn" type="text">
                风险解除
              </Button>
            </Popconfirm>
          )}
          <Button className="btn" type="text" onClick={() => showComment(record)} style={{ marginRight: '8px' }}>
            评论管理
          </Button>
          <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => del(record)}>
            <Button className="btn" type="text" status="danger">
              删除
            </Button>
          </Popconfirm>
        </>
      )
    }
  ];

  const getList = (reset: boolean, overrides: Record<string, any> = {}) => {
    if (reset) {
      setPageNum(1);
    }
    const obj: any = form.getFieldsValue();
    const pagination: any = {
      ...obj,
      pageSize,
      pageNum: reset ? 1 : pageNum,
      ...overrides
    };
    // 从日期范围中提取开始时间和结束时间
    if (obj.dateRange && Array.isArray(obj.dateRange) && obj.dateRange.length === 2) {
      pagination.startTime = dayjs(obj.dateRange[0]).format('YYYY-MM-DD HH:mm:ss');
      pagination.endTime = dayjs(obj.dateRange[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss');
    }
    // 删除 dateRange 字段，避免提交到后端
    delete pagination.dateRange;
    const finalRiskLevel = pagination.riskLevel !== undefined ? pagination.riskLevel : riskLevel;
    if (finalRiskLevel && finalRiskLevel !== DEFAULT_RISK_LEVEL) {
      pagination.riskLevel = finalRiskLevel;
    } else {
      delete pagination.riskLevel;
    }

      get(`/comment/list`, pagination)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total } = data;
          setTotal(total);
          setDataList(list || []);
        } else {
          Message.error(message + "：请求失败");
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      });
  };

  const handleReset = () => {
    form.resetFields();
    setRiskLevel(DEFAULT_RISK_LEVEL);
    setPageNum(1);
    setPageSize(20);
    const pagination: any = {
      pageSize: 20,
      pageNum: 1
    };
    get(`/comment/list`, pagination)
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          const { list, total } = data;
          setTotal(total);
          setDataList(list || []);
        } else {
          Message.error(message + "：请求失败");
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      });
  };

  const showComment = (record: any) => {
    // 回复行的 parentId 是父评论 id，优先使用后端返回的根笔记 id
    const notebookId = record.notebookId || record.parentId || record.id;
    setCommentVisible(true);
    commentId.current = notebookId;
    currentCommentId.current = record.level === 3 ? record.parentId : record.id; // 保存当前评论ID，回复行定位到父评论
    getNotebookInfo();
    getCommentList();
  };

  const getNotebookInfo = () => {
    get('/notebook/getNotebookInfoById', { id: commentId.current })
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setNotebookInfo(data);
        } else {
          Message.error(message || '获取笔记信息失败');
        }
      })
      .catch(e => {
        Message.error('获取笔记信息失败!');
        console.log(e);
      });
  };

  const getCommentList = () => {
    get('/comment/getCommentListById', { id: commentId.current })
      .then((res: any) => {
        const { code, data, message } = res || {};
        if (code === 200) {
          setCommentList(data || []);
        } else {
          Message.error(message);
        }
      })
      .catch(e => {
        Message.error('网络错误!');
        console.log(e);
      });
  };

  const delComment = (record: any) => {
    post(`/comment/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          // 判断删除的是否是当前评论
          if (currentCommentId.current && record.id === currentCommentId.current) {
            Message.success('删除当前评论成功');
            closeComment();
            getList(false); // 重置主列表
          } else {
            // 更新评论量，减1
            if (notebookInfo && notebookInfo.commentNum !== undefined) {
              setNotebookInfo({
                ...notebookInfo,
                commentNum: Math.max(0, (notebookInfo.commentNum || 0) - 1)
              });
            }
            getCommentList();
          }
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const closeComment = () => {
    setCommentVisible(false);
    setCommentList([]);
    setNotebookInfo(null);
    currentCommentId.current = null; // 重置当前评论ID
  };

  const showReply = (record: any) => {
    setReplyList(record.replyList || []);
    setReplyVisible(true);
  };

  const closeReply = () => {
    setReplyVisible(false);
    setReplyList([]);
  };

  const delReply = (record: any) => {
    post(`/comment/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          closeReply();
          getCommentList();
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const commentColumns: TableColumnProps[] = [
    {
      title: '评论选手',
      dataIndex: 'gameName',
      width: 100,
      render: (text, record: any) => {
        const isCurrentComment = currentCommentId.current && record.id === currentCommentId.current;
        return (
          <Typography.Text style={{ fontWeight: isCurrentComment ? 'bold' : 'normal', color: isCurrentComment ? '#000' : '#888' }}>
            {text}
          </Typography.Text>
        );
      }
    },
    {
      title: '评论内容',
      dataIndex: 'content',
      width: 300,
      render: (text, record: any) => {
        const isCurrentComment = currentCommentId.current && record.id === currentCommentId.current;
        return (
          <Typography.Text style={{ fontWeight: isCurrentComment ? 'bold' : 'normal' }}>
            {text}
          </Typography.Text>
        );
      }
    },
    {
      title: '评论时间',
      dataIndex: 'createTime',
      width: 150,
      render: text => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '回复数',
      dataIndex: 'replyNum',
      width: 80,
      render: text => text || 0
    },
    {
      title: '操作',
      dataIndex: 'commentAction',
      width: 150,
      render: (_, record: any) => {
        const isCurrentComment = currentCommentId.current && record.id === currentCommentId.current;
        return (
          <>
            <Button
              className="btn"
              type="text"
              disabled={!record.replyList || record.replyList.length === 0}
              onClick={() => showReply(record)}
            >
              回复管理
            </Button>
            <Popconfirm
              focusLock 
              title="提示" 
              content={isCurrentComment ? "是否删除当前评论?" : "是否删除?"} 
              onOk={() => delComment(record)}
            >
              <Button className="btn" type="text" status="danger">
                删除
              </Button>
            </Popconfirm>
          </>
        );
      }
    }
  ];

  // 回复列表弹框列定义
  const replyColumns: TableColumnProps[] = [
    {
      title: '回复人',
      dataIndex: 'gameName',
      width: 100
    },
    {
      title: '被回复',
      dataIndex: 'replyToGameName',
      width: 100,
      render: text => text ? `@${text}` : '--'
    },
    {
      title: '内容',
      dataIndex: 'content',
      width: 300
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      width: 150,
      render: text => dayjs(text).format('YYYY-MM-DD HH:mm:ss')
    },
    {
      title: '操作',
      dataIndex: 'replyAction',
      width: 80,
      render: (_, record: any) => (
        <Popconfirm focusLock title="提示" content="是否确认删除?" onOk={() => delReply(record)}>
          <Button className="btn" type="text" status="danger">
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  const del = (record: Item) => {
      post(`/comment/delete?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('删除成功！');
          if (dataList.length > 1) {
            getList(false);
          } else {
            setPageNum(pageNum - 1);
          }
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const confirmRiskComment = (record: Item) => {
    if (!record.id) {
      Message.error('评论ID不存在！');
      return;
    }
    post(`/comment/confirmRiskComment?id=${record.id}`)
      .then((res: any) => {
        const { code, message } = res || {};
        if (code === 200) {
          Message.success('操作成功！');
          getList(false);
        } else {
          Message.error(message);
        }
      })
      .catch(() => {
        Message.error('网络错误!');
      });
  };

  const handleRiskLevelChange = (val: string) => {
    const next = val ?? DEFAULT_RISK_LEVEL;
    setRiskLevel(next);
    if (pageNum === 1) {
      getList(false, { riskLevel: next });
    } else {
      setPageNum(1);
    }
  };

  useEffect(() => {
    getList(false);
  }, [pageNum, pageSize]);

  return (
    <div className="comment table-flex">
      <Form style={{ width: '100%' }} autoComplete="off" layout="inline" form={form}>
        <FormItem label="评论选手" field="gameName">
          <Input style={{ width: 120 }} />
        </FormItem>
        <FormItem label="评论内容" field="content">
          <Input style={{ width: 200 }} />
        </FormItem>
        <FormItem label="时间范围" field="dateRange">
          <DatePicker.RangePicker 
            style={{ width: 300 }} 
            disabledDate={(current) => current && current > dayjs().endOf('day')}
          />
        </FormItem>
        <FormItem label="风险等级">
          <Select
            value={riskLevel}
            onChange={handleRiskLevelChange}
            style={{ width: 120 }}
          >
            {riskLevelOption.map(opt => (
              <Option key={opt.value} value={opt.value}>
                {opt.label}
              </Option>
            ))}
          </Select>
        </FormItem>
        <FormItem>
          <Button type="primary" onClick={() => getList(true)}>
            查询
          </Button>
        </FormItem>
        <FormItem>
          <Button type="outline" onClick={handleReset}>
            重置
          </Button>
        </FormItem>
      </Form>
      <div className="table-content">
        <Table
          rowKey="id"
          columns={columns}
          data={dataList}
          className="table-scroll"
          pagination={{
            sizeCanChange: true,
            pageSize,
            total,
            showTotal: true,
            current: pageNum,
            onChange: (num, size) => {
              setPageNum(num);
              setPageSize(size);
            }
          }}
          scroll={{ y: '100%', x: '100%' }}
        />
      </div>
      <Modal
        title="评论管理"
        visible={commentVisible}
        style={{ width: 800 }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={closeComment}
        onOk={closeComment}
        unmountOnExit
      >
        {notebookInfo && (
          <div style={{
            border: '1px solid #e5e5e5',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
            background: '#fff'
          }}>
            <div style={{ marginBottom: '12px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', marginBottom: '4px' }}>
                  <img
                    src={notebookInfo.profilePic || 'https://fulicupdev.oss-cn-hangzhou.aliyuncs.com/profilePic/defaultProfile.png'}
                    alt="头像"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      marginRight: '12px',
                      objectFit: 'cover'
                    }}
                  />
                  <Typography.Text style={{ fontSize: '14px', marginRight: '8px', fontWeight: 'bold', lineHeight: '1.5' }}>
                    {notebookInfo.gameName || '未知用户'}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: '14px', lineHeight: '1.5' , color: '#333' }}>
                    {notebookInfo.createTime ? dayjs(notebookInfo.createTime).format('MM-DD HH:mm') : ''}
                  </Typography.Text>
                </div>
                {notebookInfo.title && (
                  <Typography.Text style={{ fontSize: '15px', fontWeight: '500', marginBottom: '8px', marginTop: '12px', display: 'block' }}>
                    {notebookInfo.title}
                  </Typography.Text>
                )}
                {notebookInfo.content && (
                  <Typography.Text style={{ fontSize: '14px', marginBottom: '12px', display: 'block', lineHeight: '1.5', color: '#333' }}>
                    {notebookInfo.content}
                  </Typography.Text>
                )}
                {notebookInfo.picture && Array.isArray(notebookInfo.picture) && notebookInfo.picture.length > 0 && (
                  <div className='picture-container'>
                    {notebookInfo.picture.length === 1 ? (
                      <Image
                        src={notebookInfo.picture[0]}
                        alt="笔记图片"
                        preview={true}
                      />
                    ) : (
                      <Carousel
                        autoPlay={false}
                        indicatorPosition="outer"
                        style={{ width: '100%' }}
                      >
                        {notebookInfo.picture.map((pic: string, index: number) => (
                          <div key={index} style={{ textAlign: 'center', width: '100%' }}>
                            <Image
                              src={pic}
                              alt={`笔记图片${index + 1}`}
                              preview={true}
                            />
                          </div>
                        ))}
                      </Carousel>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    👍 点赞量{notebookInfo.likeNum || 0}
                  </Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    💬 评论量{notebookInfo.commentNum || 0}
                  </Typography.Text>
                </div>
              </div>
            </div>
          </div>
        )}
        <div style={{ height: notebookInfo ? 'calc(70vh - 200px)' : '70vh' }}>
          <Table
            rowKey="id"
            columns={commentColumns}
            className="table-scroll"
            data={commentList}
            pagination={false}
            scroll={{ y: '100%', x: '100%' }}
          />
        </div>
      </Modal>
      <Modal
        title="回复管理"
        visible={replyVisible}
        style={{ width: 1000 }}
        cancelButtonProps={{ style: { display: 'none' } }}
        onCancel={closeReply}
        onOk={closeReply}
        unmountOnExit
      >
        <Table
          rowKey="id"
          columns={replyColumns}
          data={replyList}
          pagination={false}
          scroll={{ y: 1000 }}
        />
      </Modal>
    </div>
  );
}

export default Composition;
